// ── Finish Sheet (Paint / Fabric / Metal) — print document, MOZI template ────
// Same design as the Manufacturing Order print (mozPrint.js). Fabric and metal
// print landscape (many columns), paint portrait. Only saved data is printed.
import {
  esc, has, fmtDate, fmtNum, thumbCell, heroHtml, tabHtml, detailsHtml, signoffHtml, buildMozDocument, brandWordOf,
} from './mozPrint';

const LABELS = { paint: 'Paint', fabric: 'Fabric', metal: 'Metal' };

const materialLabel = e => !e?.material_type ? ''
  : e.material_type === 'other' ? `Other: ${e.material_other || ''}`
  : e.material_type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());

// Column set per finish type: [header, width, cell(row, entry)]
const COLUMNS = {
  paint: [
    ['Paint Finish Name', '30mm', (r, e) => esc(e.paint_finish_name || '')],
    ['Paint Finish Image', '26mm', r => thumbCell(r.entry?.images, '—', true), 'im'],
    ['Remarks', '', (r, e) => esc(e.remark || ''), 'rm'],
  ],
  fabric: [
    ['Fabric Name', '34mm', (r, e) => esc(e.fabric_name || '')],
    ['Fabric Image', '26mm', r => thumbCell(r.entry?.images, '—', true), 'im'],
    ['Per Item Qty', '18mm', (r, e) => esc(has(e.per_item_fabric_qty) ? `${fmtNum(e.per_item_fabric_qty)} ${e.fabric_unit || 'MTR'}` : ''), 'c'],
    ['Total Qty', '20mm', (r, e) => has(e.per_item_fabric_qty)
      ? `<b>${esc(fmtNum(partQty(r) * Number(e.per_item_fabric_qty)))}</b> ${esc(e.fabric_unit || 'MTR')}` : '', 'c'],
    ['Fabric Sample', '26mm', r => thumbCell(r.entry?.sample_images, '—', true), 'im'],
    ['Remarks', '', (r, e) => esc(e.remark || ''), 'rm'],
  ],
  metal: [
    ['Material', '24mm', (r, e) => esc(materialLabel(e))],
    ['Metal Finish', '32mm', (r, e) => esc(e.metal_finish_details || '')],
    ['Size', '26mm', (r, e) => esc([e.size_length && `L ${e.size_length}`, e.size_width && `W ${e.size_width}`,
      e.size_thickness && `T ${e.size_thickness}`, e.size_other].filter(Boolean).join(' × '))],
    ['Images', '26mm', r => thumbCell(r.entry?.images, '—', true), 'im'],
    ['Remarks', '', (r, e) => esc(e.remark || ''), 'rm'],
  ],
};

// Qty of the row that needs this finish (Paint / Fabric can be part of the row's Qty)
const finishQty = item => Number(item.finish_qty ?? item.qty) || 0;
// Qty of the line being printed: the part with the finish, or the rest without it
const partQty = r => (r.part === 'without' ? Number(r.item.rest_qty) || 0 : finishQty(r.item));

const SHEET_STATUS = { draft: 'Draft', submitted: 'Submitted' };

/**
 * data:    GET /main-pos/:id/finish/:type → { po, sheet, rows: [{ item, entry }] }
 * entries: the page's current entries keyed by main_po_item_id (what is on screen)
 * remarks: the page's current sheet remarks
 */
export function buildFinishSheetHtml(data, type, entries = {}, remarks = '', settings = {}, origin = window.location.origin) {
  const { po, sheet, rows = [] } = data;
  const label = LABELS[type] || 'Paint';
  const cols = COLUMNS[type] || COLUMNS.paint;
  const landscape = type === 'fabric' || type === 'metal';
  const status = sheet?.status || 'draft';

  const details = [
    ['Manufacturing Order No.', po.po_no, ' big'],
    ['Order Date', fmtDate(po.order_date)],
    ['Client / Project', [po.client_name, po.project_name].filter(has).join(' / ')],
    ['Timelines', po.timelines],
    ['PMC Name', po.pmc_name],
    ['Project Code', po.linked_project_code],
    ['Sheet Status', SHEET_STATUS[status] || status],
    ['Items', String(rows.length)],
  ];

  const word = label.toLowerCase();
  const line = (r, i, part) => {
    const without = part === 'without';
    const pr = without ? { ...r, part, entry: r.rest_entry } : r;
    const e = (without ? entries[`${r.item.id}:without`] : entries[r.item.id]) || pr.entry || {};
    const split = Number(r.item.rest_qty) > 0;
    const dims = (r.item.size_length || r.item.size_width || r.item.size_thickness)
      ? `L ${fmtNum(r.item.size_length) || '—'} × W ${fmtNum(r.item.size_width) || '—'} × T ${fmtNum(r.item.size_thickness) || '—'} mm` : '';
    const tagHtml = split ? `<div class="psub"><b>${without ? `Without ${esc(word)}` : `With ${esc(word)}`}</b></div>` : '';
    return `<tr>
      <td class="c sn">${without ? '' : i + 1}</td>
      <td class="pc">${without ? '' : esc(r.item.proto_code || '')}</td>
      <td class="pn">
        ${without ? `<div class="pname">${esc(r.item.product_name)}</div>${tagHtml}` : `
        <div class="pname">${esc(r.item.product_name)}</div>
        ${tagHtml}
        ${has(r.item.area) ? `<div class="psub">Area: ${esc(r.item.area)}</div>` : ''}
        ${has(r.item.product_size) ? `<div class="psub">Size: ${esc(r.item.product_size)}</div>` : ''}
        ${dims ? `<div class="psub">${esc(dims)}</div>` : ''}`}
      </td>
      <td class="im">${without ? '' : thumbCell(r.item.ref_images, 'No image', true)}</td>
      <td class="c qty">${esc(fmtNum(partQty(pr)))}</td>
      ${cols.map(([, , cell, cls]) => `<td class="${cls || ''}">${cell(pr, e)}</td>`).join('')}
    </tr>`;
  };
  const body = rows.map((r, i) => line(r, i, 'with') + (Number(r.item.rest_qty) > 0 ? line(r, i, 'without') : '')).join('');

  const colCount = 5 + cols.length;
  const totalQty = rows.reduce((s, r) => s + finishQty(r.item) + (Number(r.item.rest_qty) || 0), 0);
  const tableTitle = `${label} Finish Details`;

  const html = `
  ${heroHtml({ brandWord: brandWordOf(settings), titleTop: `${label} Finish`, titleAccent: 'Sheet', sub: `Manufacturing Order ${po.po_no || ''}` })}

  ${tabHtml('doc', 'Order Details', '58mm')}
  ${detailsHtml(details)}

  ${tabHtml('box', esc(tableTitle), landscape ? '78mm' : '72mm')}
  <table class="items">
    <thead><tr>
      <th class="c sn">S.No.</th><th style="width:22mm">Proto Code</th><th style="width:${landscape ? '58mm' : '44mm'}">Product Name &amp; Description</th>
      <th class="c" style="width:26mm">Image (Reference)</th><th class="c" style="width:11mm">Qty.</th>
      ${cols.map(([h, w, , cls]) => `<th class="${cls === 'c' || cls === 'im' ? 'c' : ''}"${w ? ` style="width:${w}"` : ''}>${esc(h)}</th>`).join('')}
    </tr></thead>
    <tbody>${body || `<tr><td colspan="${colCount}" class="none">No items on this Manufacturing Order are marked for ${esc(label.toLowerCase())} finish</td></tr>`}</tbody>
    ${rows.length ? `<tfoot><tr><td colspan="4" class="tl">Total Items: ${rows.length}</td><td class="c qty">${esc(fmtNum(totalQty))}</td><td colspan="${cols.length}"></td></tr></tfoot>` : ''}
  </table>

  ${signoffHtml([['Prepared By', ''], ['Checked By', ''], ['Approved By', '']], remarks)}`;

  return buildMozDocument({ docTitle: `${po.po_no || ''} — ${label} Finish Sheet`, body: html, settings, origin, landscape });
}
